import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Printer, Search } from 'lucide-react';
import { FeesApi, FeeReportStudentRow, OrgFeesApi } from '@/services/fees.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';
import { fmtMoney } from './lib/feeFormat';
import { printStudentRow } from './lib/feeReportPrint';

const columnHelper = createColumnHelper<FeeReportStudentRow>();

/**
 * At `scope="organization"` this is reachable by an org-admin for ANY
 * school in scope via an explicit School field — matching real legacy's
 * Super-Admin-sees-everything-with-an-inline-School-dropdown pattern —
 * instead of switching the whole session into that school first (the old
 * `/org/select-school` redirect). See OrgFeesApi/org-fees.controller.ts.
 */
export function FeesPage({ scope = 'school' }: { scope?: 'organization' | 'school' }) {
  const term = useTerminology();
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'studentName', defaultSortOrder: 'asc' });
  const showSchoolPicker = scope === 'organization';

  const [schoolId, setSchoolId] = useState('');
  // 'all' — aggregate every year for the school, including demands whose
  // academic_year_id is NULL (a schema-per-tenant migration artifact; see
  // docs/data-issues.md) — is the default, mirroring the old system's
  // combined view. A specific year never includes those undated demands.
  const [yearId, setYearId] = useState('all');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'all' | 'paid' | 'unpaid'>('all');

  const { data: schools = [] } = useQuery({
    queryKey: ['org-fees-schools'],
    queryFn: OrgFeesApi.schools,
    enabled: showSchoolPicker,
  });

  const haveTarget = !showSchoolPicker || !!schoolId;

  const { data: defaultYear, isLoading: defaultYearLoading } = useQuery({
    queryKey: ['fees', 'default-year', schoolId],
    queryFn: () => (showSchoolPicker ? OrgFeesApi.defaultYear(schoolId) : FeesApi.defaultYear()),
    enabled: haveTarget,
  });
  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-fees-selector', schoolId],
    queryFn: () =>
      showSchoolPicker
        ? OrgFeesApi.academicYears(schoolId)
        : AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
    enabled: haveTarget,
  });
  const years = yearsPage?.items ?? [];
  const effectiveYearId = yearId;
  const hasAnyData = !!defaultYear;

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-fees-selector', schoolId],
    queryFn: () => (showSchoolPicker ? OrgFeesApi.classes(schoolId) : AcademicClassesApi.list({ limit: 100 })),
    enabled: haveTarget,
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const reportParams = {
    ...dt.params,
    academicYearId: effectiveYearId,
    classId: classId || undefined,
    sectionId: sectionId || undefined,
    paymentStatus,
  };

  const { data: report, isFetching } = useQuery({
    queryKey: ['fees', 'report', schoolId, reportParams],
    queryFn: () => (showSchoolPicker ? OrgFeesApi.report(schoolId, reportParams) : FeesApi.report(reportParams)),
    enabled: !defaultYearLoading && haveTarget,
    placeholderData: keepPreviousData,
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No',
      cell: (ctx) => (dt.page - 1) * dt.limit + ctx.row.index + 1,
    }),
    columnHelper.accessor('studentName', { header: 'Student Name' }),
    columnHelper.accessor('className', { header: 'Class Name' }),
    columnHelper.display({
      id: 'sessionYear',
      header: 'Session Year',
      cell: () =>
        effectiveYearId === 'all'
          ? 'All Years'
          : (years.find((y) => y.id === effectiveYearId)?.name ?? '—'),
    }),
    columnHelper.accessor('demanded', { header: 'Demanded', cell: (ctx) => fmtMoney(ctx.getValue()) }),
    columnHelper.accessor('paid', { header: 'Collected', cell: (ctx) => fmtMoney(ctx.getValue()) }),
    columnHelper.accessor('pending', {
      header: 'Pending',
      cell: (ctx) => (
        <Badge tone={ctx.getValue() <= 0 ? 'green' : 'amber'}>{fmtMoney(ctx.getValue())}</Badge>
      ),
    }),
    columnHelper.display({
      id: 'print',
      header: '',
      cell: (ctx) => (
        <button
          type="button"
          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          title="Print"
          onClick={() => printStudentRow(ctx.row.original)}
        >
          <Printer className="h-4 w-4" />
        </button>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Fee Report"
        description="School fee collection overview — totals, per-student demand/collection, and printable summaries."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={!hasAnyData}
              onClick={() =>
                showSchoolPicker
                  ? OrgFeesApi.classSummaryPdf('pdf', schoolId, reportParams)
                  : FeesApi.classSummaryPdf('pdf', reportParams)
              }
            >
              Class Summary PDF
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={!hasAnyData}
              onClick={() =>
                showSchoolPicker
                  ? OrgFeesApi.paidUnpaidPdf('pdf', schoolId, reportParams)
                  : FeesApi.paidUnpaidPdf('pdf', reportParams)
              }
            >
              Paid/Unpaid Summary PDF
            </button>
          </div>
        }
      />

      {/* Summary cards */}
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Fees</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">{fmtMoney(report?.totalDemanded ?? 0)}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Collected Fees</div>
          <div className="mt-1 text-2xl font-semibold text-green-600">{fmtMoney(report?.totalCollected ?? 0)}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Pending Fees</div>
          <div className="mt-1 text-2xl font-semibold text-amber-600">{fmtMoney(report?.totalPending ?? 0)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {showSchoolPicker && (
          <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            <option value="">— Select a school —</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        )}
        {haveTarget && (
          <>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={dt.searchInput}
                onChange={(e) => dt.setSearchInput(e.target.value)}
                placeholder="Search by name or admission #"
                className="pl-9"
              />
            </div>
            <Select className="!w-40" value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(''); }}>
              <option value="">All {term.level}</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Select className="!w-40" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
              <option value="">All {term.group}</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
            <Select className="!w-44" value={yearId} onChange={(e) => setYearId(e.target.value)}>
              <option value="all">All Years</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </Select>
            <Select
              className="!w-36"
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value as 'all' | 'paid' | 'unpaid')}
            >
              <option value="all">All</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </Select>
          </>
        )}
      </div>

      {!haveTarget ? (
        <div className="card p-8 text-center text-slate-500">Select a school to view its fee report.</div>
      ) : !hasAnyData && !defaultYearLoading ? (
        <div className="card p-8 text-center text-slate-500">
          No fee data. This is expected until the fee migration is committed for this school.
        </div>
      ) : (
        <DataTable
          columns={columns}
          result={report?.students}
          state={dt}
          loading={isFetching}
          emptyMessage="No fee data for the selected filters."
          getRowId={(row) => row.studentId}
        />
      )}
    </>
  );
}
